import { Card } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconShoppingBag } from '@tabler/icons-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'src/components/Button/Button'
import { Headline } from 'src/components/Headline/Headline'
import { Modal } from 'src/components/Modal/Modal'
import { ProductCardPrice } from 'src/components/ProductCard/ProductCardPrice'
import { ShopifyImage } from 'src/components/ShopifyImage/ShopifyImage'
import { SimilarProductModalContent } from 'src/components/SimilarProducts/SimilarProductModalContent'
import { Text } from 'src/components/Text/Text'
import { useLocale } from 'src/contexts/LocaleContext'
import { getCheapestVariant } from 'src/functions/product/getCheapestVariant'
import { removeBioOrganic } from 'src/functions/product/removeBioOrganic'
import { getMetafield } from 'src/functions/util/getMetafield'
import { useCartQuery } from 'src/hooks/useCartQuery'

import styles from './SimilarProductCard.module.scss'

import type { Product } from 'src/data/graphql-generated'

interface SimilarProductCardProps {
  product: Product
}

export const SimilarProductCard = ({ product }: SimilarProductCardProps) => {
  const { locale } = useLocale()
  const { t } = useTranslation()
  const { data: cart } = useCartQuery()

  const [similarProductModalOpened, { open: openSimilarProductModal, close: closeSimilarProductModal }] =
    useDisclosure()

  useEffect(() => {
    closeSimilarProductModal()
  }, [cart])

  const {
    title,
    metafields,
    vendor,
    variants: { nodes: productVariants },
    images: { nodes: productImages },
  } = product

  const [firstImage] = productImages
  const productTitleWithoutBio = removeBioOrganic(title)
  const coffeeTaste = getMetafield(metafields, 'coffee', 'taste', locale)
    ?.map((taste: Record<string, string>) => taste?.title)
    .join(', ')

  const hasMultipleVariants = productVariants.length > 1
  const secondaryText = coffeeTaste || vendor || 'Coffee Circle'
  const displayedVariant = getCheapestVariant(productVariants)

  return (
    <>
      <Card className={styles.card} onClick={openSimilarProductModal}>
        <ShopifyImage image={firstImage} width={90} height={90} alt="" lazy />
        <div className={styles.content}>
          <Headline variant="h3" size="h4" className={styles.title}>
            {productTitleWithoutBio}
          </Headline>
          <Text variant="span" small>
            {secondaryText}
          </Text>
          <ProductCardPrice productVariant={displayedVariant} isStartingPrice={hasMultipleVariants} />
          <div>
            <Button
              onClick={openSimilarProductModal}
              variant="tertiary"
              startIcon={<IconShoppingBag />}
              inline
              data-analytics-id="add-to-cart-cross-selling"
            >
              {t('common.addToCart')}
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        opened={similarProductModalOpened}
        title={t('pdp.similarProducts.modalTitle')}
        onClose={closeSimilarProductModal}
      >
        <SimilarProductModalContent product={product} />
      </Modal>
    </>
  )
}
